import React, { useState } from 'react';
import { Typography, TextField, Tooltip, Dialog, DialogTitle, DialogContent, useMediaQuery, useTheme } from '@mui/material';
import { 
  SectionContainer, 
  HeaderContainer, 
  StyledRating, 
  StyledAccountCircleOutlinedIcon, 
  StyledChatBubbleOutlineOutlinedIcon, 
  StyledAccessTimeOutlinedIcon, 
  StyledAttachMoneyOutlinedIcon, 
  StyledMenuBookOutlinedIcon,
  RatingWrapper,
  StyledHelpIcon,
  ModalButtonContainer
} from '../FeedbackPage.styles';
import { SimpleModal } from 'components/Modal';
import ModalTitleContainer from "components/Modal/components/ModalTitleContainer";
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';

interface RatingSectionProps {
  title: string;
  description: string;
  value: number | null;
  comment: string;
  type: 'clinical' | 'professional' | 'communication' | 'time' | 'value';
  onChange: (rating: number | null) => void;
  onCommentChange: (comment: string) => void;
}

const getIcon = (type: string) => {
  switch (type) {
    case 'clinical':
      return <StyledMenuBookOutlinedIcon />;
    case 'professional':
      return <StyledAccountCircleOutlinedIcon />;
    case 'communication':
      return <StyledChatBubbleOutlineOutlinedIcon />;
    case 'time':
      return <StyledAccessTimeOutlinedIcon />;
    case 'value':
      return <StyledAttachMoneyOutlinedIcon />;
    default:
      return null;
  }
};

const getHelpText = (type: string) => {
  switch (type) {
    case 'clinical':
      return "Rate the hygienist's technical skills, knowledge of dental procedures, and ability to perform clinical tasks effectively.";
    case 'professional':
      return "Evaluate the hygienist's appearance, punctuality, and overall professional conduct in the workplace.";
    case 'communication':
      return "Assess how well the hygienist interacts with patients and staff, including their ability to explain procedures and maintain a comfortable environment.";
    case 'time':
      return "Rate the hygienist's ability to manage their schedule, complete tasks efficiently, and respect appointment times.";
    case 'value':
      return "Consider if the quality of work and service provided matches the hourly rate charged.";
    default:
      return "";
  }
};

const RatingSection: React.FC<RatingSectionProps> = ({
  title,
  description,
  value,
  comment,
  type,
  onChange,
  onCommentChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isMobile = window.cordova ? true : false;
  const helpText = getHelpText(type);

  const handleHelpClick = () => {
    setIsModalOpen(true);
  };

  const renderRatingWithHelp = () => {
    const helpIcon = (
      <div className="no-fastclick">
        {isMobile ? (
          <StyledHelpIcon onClick={handleHelpClick} />
        ) : (
          <Tooltip title={helpText} arrow placement="top">
            <StyledHelpIcon onClick={handleHelpClick} />
          </Tooltip>
        )}
      </div>
    );

    return (
      <RatingWrapper>
        <div className="rating-help-container">
          <StyledRating
            name={`rating-${type}`}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            precision={1}
            className="no-fastclick"
            />
          {helpIcon}
        </div>
      </RatingWrapper>
    );
  };

  return (
    <SectionContainer>
      <HeaderContainer>
        {getIcon(type)}
        <Typography variant="h6" component="h3">
          {title}
        </Typography>
      </HeaderContainer>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        {description}
      </Typography>

      {renderRatingWithHelp()}

      <TextField
        fullWidth
        multiline
        rows={2}
        variant="outlined"
        placeholder={`Provide details on ${title.toLowerCase()}...`}
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        sx={{ 
          marginTop: 2,
          '& .MuiOutlinedInput-root': {
            minHeight: { xs: 'auto', sm: '100px' },
          },
          '& .MuiInputBase-input': {
            fontSize: { xs: '14px', sm: '15px' },
            lineHeight: { xs: '1.4', sm: '1.4' },
          },
          '& .MuiOutlinedInput-input': {
            padding: { xs: '8px 12px', sm: '10px 12px' },
          }
        }}
      />

      <SimpleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalTitleContainer 
          title={title}
          onClose={() => setIsModalOpen(false)}
        />
        <DialogContent>
          <Typography align="left" gutterBottom>
            {helpText}
          </Typography>
        </DialogContent>
        <ModalButtonContainer>
          <StyledButton
            variant="contained"
            onClick={() => setIsModalOpen(false)}
          >
            OK
          </StyledButton>
        </ModalButtonContainer>
      </SimpleModal>
    </SectionContainer>
  );
};

export default RatingSection;
